import type { ApiError } from "./client"
import useCustomToast from "./hooks/useCustomToast"

/**
 * Resolves a stored logo_url (which may be a relative backend path) to a full URL.
 * The backend stores paths like "/api/v1/uploads/logos/{id}.png". Since Vite has no
 * proxy for /api, we must prepend VITE_API_URL so the browser reaches the backend.
 */
export const resolveLogoUrl = (
  url: string | null | undefined,
): string | null => {
  if (!url) return null
  if (url.startsWith("http")) return url
  const base = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "")
  return `${base}${url}`
}

export const emailPattern = {
  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  message: "Invalid email address",
}

export const namePattern = {
  value: /^[A-Za-z\s\u00C0-\u017F]{1,30}$/,
  message: "Invalid name",
}

interface PasswordRules {
  minLength: { value: number; message: string }
  required?: string
}

interface ConfirmPasswordRules {
  validate: (value: string) => true | string
  required?: string
}

export const passwordRules = (isRequired = true): PasswordRules => {
  const rules: PasswordRules = {
    minLength: {
      value: 8,
      message: "Password must be at least 8 characters",
    },
  }

  if (isRequired) {
    rules.required = "Password is required"
  }

  return rules
}

export const confirmPasswordRules = (
  getValues: () => any,
  isRequired = true,
): ConfirmPasswordRules => {
  const rules: ConfirmPasswordRules = {
    validate: (value: string) => {
      const password = getValues().password || getValues().new_password
      return value === password ? true : "The passwords do not match"
    },
  }

  if (isRequired) {
    rules.required = "Password confirmation is required"
  }

  return rules
}

export const handleError = (err: ApiError) => {
  const { showErrorToast } = useCustomToast()
  const body = err.body as { detail?: string | Array<{ msg: string }> } | null
  const errDetail = body?.detail
  let errorMessage =
    typeof errDetail === "string" ? errDetail : "Something went wrong."
  if (Array.isArray(errDetail) && errDetail.length > 0) {
    errorMessage = errDetail[0].msg
  }
  showErrorToast(errorMessage)
}
